import React, { useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Plus, Search, Folder, FolderPlus, ChevronRight, ChevronDown,
  MoreHorizontal, Archive, Pencil, Briefcase, Cable, ShieldCheck,
  LogOut, X, Star, Sun, ListChecks, Brain, FileText, CheckSquare, CalendarClock,
  AtSign, Inbox, Target, Scale, Rocket,
} from "lucide-react";
import StellaLogo from "./StellaLogo";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Sidebar({
  chats, projects, activeChatId, onSelectChat, onNewChat,
  onRefreshChats, onRefreshProjects, onClose,
}) {
  const [q, setQ] = useState("");
  const [workplaceOpen, setWorkplaceOpen] = useState(false);
  const [projOpen, setProjOpen] = useState(true);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectDraft, setProjectDraft] = useState({ name: "", instructions: "" });
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [brainOpen, setBrainOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const filteredChats = useMemo(() => {
    if (!q.trim()) return chats || [];
    const needle = q.toLowerCase();
    return (chats || []).filter((c) => (c.title || "").toLowerCase().includes(needle));
  }, [chats, q]);

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", projectDraft);
      setShowCreateProject(false);
      setProjectDraft({ name: "", instructions: "" });
      await onRefreshProjects?.();
      toast.success("Project created");
    } catch (err) {
      toast.error(errText(err, "Could not create project"));
    }
  };

  const submitRename = async (chat) => {
    try {
      await api.patch(`/chats/${chat.id}`, { title: renameValue.trim() || "Untitled" });
      setRenamingId(null);
      await onRefreshChats?.();
    } catch {
      toast.error("Rename failed");
    }
  };

  const archiveChat = async (chat) => {
    try {
      await api.delete(`/chats/${chat.id}`);
      await onRefreshChats?.();
      toast.success("Chat archived");
    } catch {
      toast.error("Archive failed");
    }
  };

  return (
    <aside
      className="h-full w-full sm:w-72 bg-[#F4F3EE]/95 backdrop-blur-md border-r border-[#E5E4DE] flex flex-col"
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 text-indigo-700">
          <StellaLogo className="w-5 h-5" />
          <span className="font-bold tracking-tight text-slate-900">Stella</span>
        </div>
        <button onClick={onClose} className="sm:hidden p-1 rounded-lg hover:bg-slate-200/60" aria-label="Close sidebar">
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </div>

      {/* Top actions */}
      <div className="px-3 space-y-2">
        <Button
          onClick={onNewChat}
          data-testid="sidebar-new-chat-button"
          className="w-full justify-start gap-2 h-10 bg-white hover:bg-slate-50 text-slate-900 border border-[#E5E4DE] rounded-xl shadow-sm"
        >
          <Plus className="w-4 h-4 text-indigo-600" />
          <span className="font-medium">New chat</span>
        </Button>
        <TopLink to="/today" label="Today" icon={<Sun className="w-4 h-4 text-amber-500" />} testid="sidebar-today-link" />
      </div>

      <div className="px-3 mt-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="sidebar-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chats"
            aria-label="Search chats in this sidebar"
            className="pl-9 h-10 bg-white/70 border-[#E5E4DE] rounded-xl"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto mt-3 px-2 pb-4 space-y-4">
        {/* Recent chats */}
        <section>
          <SectionTitle>Recent</SectionTitle>
          <div className="space-y-0.5" data-testid="recent-chats-list">
            {filteredChats.length === 0 && (
              <div className="text-xs text-slate-500 px-3 py-2">No chats yet.</div>
            )}
            {filteredChats.map((c) => (
              <div
                key={c.id}
                className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 ${
                  activeChatId === c.id ? "bg-indigo-50 text-indigo-900" : "hover:bg-slate-200/50 text-slate-700"
                }`}
              >
                {renamingId === c.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitRename(c);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    onBlur={() => submitRename(c)}
                    className="flex-1 bg-white border border-indigo-200 rounded-md px-2 py-1 text-sm outline-none"
                    data-testid={`chat-rename-input-${c.id}`}
                  />
                ) : (
                  <button
                    onClick={() => onSelectChat?.(c.id)}
                    className="flex-1 text-left truncate text-sm"
                    data-testid={`chat-item-${c.id}`}
                    title={c.title}
                  >
                    {c.title || "Untitled"}
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-white"
                      aria-label="Chat menu"
                      data-testid={`chat-menu-${c.id}`}
                    >
                      <MoreHorizontal className="w-4 h-4 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onSelect={() => { setRenamingId(c.id); setRenameValue(c.title); }}>
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => archiveChat(c)}>
                      <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </section>

        {/* Projects */}
        <section>
          <div className="flex items-center justify-between px-3">
            <button
              onClick={() => setProjOpen((v) => !v)}
              className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              data-testid="sidebar-projects-toggle"
            >
              {projOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Projects
            </button>
            <button
              onClick={() => setShowCreateProject(true)}
              className="p-1 rounded hover:bg-slate-200/60"
              aria-label="New project"
              data-testid="sidebar-new-project-button"
            >
              <FolderPlus className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          {projOpen && (
            <div className="mt-1 space-y-0.5">
              {(!projects || projects.length === 0) && (
                <div className="text-xs text-slate-500 px-3 py-2">No projects yet.</div>
              )}
              {projects?.map((p) => (
                <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 rounded-lg hover:bg-slate-200/50" data-testid={`project-item-${p.id}`}>
                  <Folder className="w-4 h-4 text-indigo-500" />
                  <span className="truncate">{p.name}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Tasks + Memory + Files as direct links */}
        <section>
          <SectionTitle>Work</SectionTitle>
          <NavLink to="/tasks"
                   className={({ isActive }) =>
                     `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                       isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                     }`}
                   data-testid="sidebar-tasks-link">
            <ListChecks className="w-4 h-4 text-indigo-500" />
            Tasks
          </NavLink>
          <NavLink to="/memory"
                   className={({ isActive }) =>
                     `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                       isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                     }`}
                   data-testid="sidebar-memory-link">
            <Brain className="w-4 h-4 text-indigo-500" />
            Memory
          </NavLink>
          <NavLink to="/files"
                   className={({ isActive }) =>
                     `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                       isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                     }`}
                   data-testid="sidebar-files-link">
            <FileText className="w-4 h-4 text-indigo-500" />
            Files
          </NavLink>
        </section>

        {/* Stella Brain — collapsible section with Goals + Decisions */}
        <section data-testid="section-stella-brain">
          <button
            onClick={() => setBrainOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-200/40 rounded-lg"
            data-testid="sidebar-brain-toggle"
          >
            {brainOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <Brain className="w-3.5 h-3.5 text-indigo-500" />
            Stella Brain
          </button>
          {brainOpen && (
            <div className="space-y-0.5">
              <NavLink to="/goals"
                       className={({ isActive }) =>
                         `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                           isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                         }`}
                       data-testid="sidebar-goals-link">
                <Target className="w-4 h-4 text-indigo-500" />
                Goals
              </NavLink>
              <NavLink to="/decisions"
                       className={({ isActive }) =>
                         `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                           isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                         }`}
                       data-testid="sidebar-decisions-link">
                <Scale className="w-4 h-4 text-indigo-500" />
                Decision Board
              </NavLink>
            </div>
          )}
        </section>

        {/* Approvals (real page) */}
        <section>
          <NavLink
            to="/missions"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
              }`
            }
            data-testid="sidebar-missions-link"
          >
            <Rocket className="w-4 h-4 text-indigo-500" />
            Missions
          </NavLink>
          <NavLink
            to="/approvals"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
              }`
            }
            data-testid="sidebar-approvals-link"
          >
            <CheckSquare className="w-4 h-4 text-indigo-500" />
            Approvals
          </NavLink>
          <NavLink
            to="/integrations"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
              }`
            }
            data-testid="sidebar-integrations-link"
          >
            <Cable className="w-4 h-4 text-indigo-500" />
            Integrations
          </NavLink>
        </section>

        {/* Settings (Gate B-EXT) */}
        <section>
          <SectionTitle>Settings</SectionTitle>
          <NavLink
            to="/settings/accounts"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
              }`
            }
            data-testid="sidebar-connected-accounts-link"
          >
            <AtSign className="w-4 h-4 text-indigo-500" />
            Connected Accounts
          </NavLink>
          <NavLink
            to="/mail"
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
              }`
            }
            data-testid="sidebar-mail-search-link"
          >
            <Inbox className="w-4 h-4 text-indigo-500" />
            Mail Search
          </NavLink>
        </section>

        {/* Coming-soon collapsible sections */}
        <ComingSoon
          label="Workplace"
          icon={<Briefcase className="w-4 h-4 text-slate-400" />}
          open={workplaceOpen}
          setOpen={setWorkplaceOpen}
          testid="section-workplace"
        />

        {user?.role === "admin" && (
          <section>
            <SectionTitle>Admin</SectionTitle>
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-700 hover:bg-slate-200/50"
                }`
              }
              data-testid="sidebar-admin-link"
            >
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              User management
            </NavLink>
          </section>
        )}
      </div>

      {/* Footer / user */}
      <div className="border-t border-[#E5E4DE] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 flex items-center justify-center text-xs font-bold shrink-0">
              {user?.email?.slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-900 truncate" data-testid="sidebar-user-email">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                {user?.role === "admin" && <Star className="w-3 h-3 text-indigo-500" />} {user?.role}
              </div>
            </div>
          </div>
          <button
            onClick={doLogout}
            className="p-2 rounded-lg hover:bg-slate-200/60 text-slate-600"
            aria-label="Log out"
            data-testid="sidebar-logout-button"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Create project modal */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent className="sm:max-w-md rounded-2xl bg-white border-[#E5E4DE]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-bold">New project</DialogTitle>
            <DialogDescription className="text-slate-500">
              Organize related chats with a shared context.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createProject} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Name</label>
              <Input
                required
                value={projectDraft.name}
                onChange={(e) => setProjectDraft({ ...projectDraft, name: e.target.value })}
                data-testid="new-project-name"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Instructions (optional)</label>
              <Textarea
                value={projectDraft.instructions}
                onChange={(e) => setProjectDraft({ ...projectDraft, instructions: e.target.value })}
                placeholder="Tone, domain, style preferences..."
                data-testid="new-project-instructions"
                className="mt-1 min-h-[100px]"
              />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="new-project-submit"
                      className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl">
                Create project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

function TopLink({ to, label, icon, testid }) {
  return (
    <NavLink to={to}
             end
             className={({ isActive }) =>
               `w-full justify-start gap-2 h-10 flex items-center px-3 rounded-xl border shadow-sm ${
                 isActive
                   ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                   : "bg-white border-[#E5E4DE] text-slate-900 hover:bg-slate-50"
               }`}
             data-testid={testid}>
      {icon}
      <span className="font-medium text-sm">{label}</span>
    </NavLink>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </div>
  );
}

function ComingSoon({ label, icon, open, setOpen, testid, hint }) {
  return (
    <section data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-200/50 rounded-lg"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {icon}
        <span className="font-medium">{label}</span>
      </button>
      {open && (
        <div className="mx-3 mt-1 px-3 py-2 text-xs text-slate-500 bg-white/60 border border-dashed border-slate-200 rounded-lg">
          {hint || "Coming in a later phase."}
        </div>
      )}
    </section>
  );
}
